import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { onboardingApi } from '../api/onboardingApi';
import type { ProfileStepData, StrategyStepData, IntegrationStepData } from '../api/onboardingApi';
import { onboardingKeys } from '../queryKeys';
import { ROUTES } from '@/config/constants';

export const ONBOARDING_STEPS = ['profile', 'strategy', 'integrations', 'complete'] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export function useOnboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<number>(0);

  const { data: statusData, isLoading } = useQuery({
    queryKey: onboardingKeys.status(),
    queryFn: () => onboardingApi.getStatus(),
    select: (res) => res.data,
  });

  const profileMutation = useMutation({
    mutationFn: (payload: ProfileStepData) => onboardingApi.saveProfile(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      setCurrentStep(1);
    },
  });

  const strategyMutation = useMutation({
    mutationFn: (payload: StrategyStepData) => onboardingApi.saveStrategy(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      setCurrentStep(2);
    },
  });

  const integrationsMutation = useMutation({
    mutationFn: (payload: IntegrationStepData) => onboardingApi.saveIntegrations(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      setCurrentStep(3);
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => onboardingApi.complete(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      navigate(ROUTES.DASHBOARD);
    },
  });

  return {
    currentStep,
    setCurrentStep,
    status: statusData,
    isLoading,
    steps: ONBOARDING_STEPS,
    saveProfile: profileMutation.mutate,
    isSavingProfile: profileMutation.isPending,
    saveStrategy: strategyMutation.mutate,
    isSavingStrategy: strategyMutation.isPending,
    saveIntegrations: integrationsMutation.mutate,
    isSavingIntegrations: integrationsMutation.isPending,
    complete: completeMutation.mutate,
    isCompleting: completeMutation.isPending,
  };
}
